if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initGalleryArrows();
        this.initThumbnailArrows();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      initGalleryArrows() {
        this.arrowPrev = this.elements.viewer.querySelector('.gallery-arrow--prev');
        this.arrowNext = this.elements.viewer.querySelector('.gallery-arrow--next');
        if (!this.arrowPrev || !this.arrowNext) return;

        this.arrowPrev.addEventListener('click', this.onGalleryArrowClick.bind(this, -1));
        this.arrowNext.addEventListener('click', this.onGalleryArrowClick.bind(this, 1));
        this.elements.viewer.addEventListener('slideChanged', () => this.updateGalleryArrows());
        this.mql.addEventListener('change', () => this.updateGalleryArrows());
        this.updateGalleryArrows();
      }

      getGalleryItems() {
        // Only the slide <li>s — the zoom-toggle buttons inside each slide also
        // carry data-media-id (numeric only) and must never be treated as slides.
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      initThumbnailArrows() {
        if (!this.elements.thumbnails) return;
        this.thumbArrowPrev = this.elements.thumbnails.querySelector('button[name="previous"]');
        this.thumbArrowNext = this.elements.thumbnails.querySelector('button[name="next"]');
        // The thumbnail-strip arrows navigate the main image (prev/next slide)
        // instead of only scrolling the strip.
        const navigate = (direction, event) => {
          event.stopImmediatePropagation();
          event.preventDefault();
          this.onGalleryArrowClick(direction, event);
        };
        this.thumbArrowPrev?.addEventListener('click', navigate.bind(this, -1));
        this.thumbArrowNext?.addEventListener('click', navigate.bind(this, 1));
      }

      getGalleryIndex(items) {
        // On mobile the list is a live slider: trust its position over the is-active
        // class, which is only kept in sync by setActiveMedia (desktop image swap).
        if (!this.mql.matches && this.elements.viewer.currentPage) {
          return Math.min(items.length - 1, Math.max(0, this.elements.viewer.currentPage - 1));
        }
        const activeIndex = items.findIndex((item) => item.classList.contains('is-active'));
        return activeIndex < 0 ? 0 : activeIndex;
      }

      onGalleryArrowClick(direction, event) {
        event.preventDefault();
        const items = this.getGalleryItems();
        if (items.length < 2) return;

        const targetIndex = this.getGalleryIndex(items) + direction;
        if (targetIndex < 0 || targetIndex >= items.length) return;

        this.setActiveMedia(items[targetIndex].dataset.mediaId, false);
        this.updateGalleryArrows(targetIndex);
      }

      updateGalleryArrows(forcedIndex) {
        if (!this.arrowPrev || !this.arrowNext) return;

        const items = this.getGalleryItems();
        if (items.length < 2) {
          this.arrowPrev.closest('.gallery-arrows')?.setAttribute('hidden', '');
          return;
        }

        const index = forcedIndex !== undefined ? forcedIndex : this.getGalleryIndex(items);
        this.arrowPrev.toggleAttribute('disabled', index <= 0);
        this.arrowNext.toggleAttribute('disabled', index >= items.length - 1);
        // Keep the thumbnail-strip arrows in sync with the same position.
        this.thumbArrowPrev?.toggleAttribute('disabled', index <= 0);
        this.thumbArrowNext?.toggleAttribute('disabled', index >= items.length - 1);
      }

      setActiveMedia(mediaId, prepend) {
        // Scope to .product__media-item: the zoom-toggle buttons inside the
        // slides also have data-media-id (numeric only) and apps may call this
        // with the bare numeric id — fall back to a "-<id>" suffix match so the
        // correct slide is activated instead of the toggle button.
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector(`.product__media-item[data-media-id$="-${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item');
        if (!activeMedia) {
          return;
        }
        const resolvedMediaId = activeMedia.dataset.mediaId;
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${resolvedMediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${resolvedMediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        // A media item may have no matching thumbnail (e.g. variant-image apps
        // inject extra slides) — never let that break the whole navigation.
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || position === undefined || position === null) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        // Never reassign image.src here: it drops the displayed bitmap and
        // re-fetches it, which blanks the main image on every arrow/thumbnail click.
        if (image.complete && image.naturalHeight !== 0) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
